import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useContent } from '@/stores/RootStore';
import { useReveal } from '@/hooks/useReveal';
import { useHeroParallax } from '@/hooks/useHeroParallax';
import { scrollToSection, getLenis } from '@/hooks/useLenis';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
import { PRODUCTS_CATALOG, PRODUCT_FILTERS, PRODUCT_DETAILS, PRODUCT_DETAIL_ROUTE, type ProductType, type ProductFeature } from '@/data/mockData';
// [2026-10-23 사용자] 제품 영역 화이트 전환(한 장씩) — /products는 화이트 전용 모듈로 격리.
//   다크 Products.module은 홈 제품 섹션(sections/Products.tsx)과 공유 → 건드리지 않음. 클래스명 1:1이라 본문 거의 무수정.
import s from './ProductsWhite.module.scss';

// [2026-09-11 사용자] 제품 상세 피처용 플랫 라인 SVG 아이콘 세트([[evs-svg-icon-sources]] 스타일: stroke 1.6, 라운드).
//   PRODUCT_DETAILS[*].features[].icon 키와 1:1. 없는 키는 기본 체크로 폴백.
const DETAIL_ICON: Record<string, React.ReactNode> = {
  wbgt: <path d="M10 13V5a2 2 0 1 1 4 0v8a4 4 0 1 1-4 0z" />,
  temp: <path d="M10 13V5a2 2 0 1 1 4 0v8a4 4 0 1 1-4 0z" />,
  alert: <><path d="M12 3.5 21 19H3z" /><path d="M12 10v4" /><path d="M12 17h.01" /></>,
  bell: <><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></>,
  record: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M9 13h6M9 17h4" /></>,
  cloud: <path d="M17.5 19a4.5 4.5 0 0 0 .5-9 6 6 0 0 0-11.6 1.6A4 4 0 0 0 6.5 19z" />,
  gas: <><path d="M12 3s6 6 6 10a6 6 0 0 1-12 0c0-4 6-10 6-10z" /><path d="M9.5 14a2.5 2.5 0 0 0 2.5 2.5" /></>,
  comm: <><path d="M5 12.5a10 10 0 0 1 14 0" /><path d="M8 15.5a6 6 0 0 1 8 0" /><path d="M12 18.5h.01" /></>,
  gauge: <><path d="M20.5 12a8.5 8.5 0 1 0-17 0" /><path d="M12 12l4-2.5" /><path d="M3.5 12h1M19.5 12h1M12 3.5v1" /></>,
  screen: <><rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M8 20h8M12 16v4" /></>,
  cert: <><path d="M12 3l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V6z" /><path d="M9 12l2 2 4-4.5" /></>,
  current: <path d="M13 2 4 14h7l-1 8 9-12h-7z" />,
  pressure: <><path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" /><path d="M12 12l3.5-2.2" /><path d="M12 8v1" /></>,
  ph: <><path d="M9 3h6M10 3v5l-5.2 9.2A2 2 0 0 0 6.6 20h10.8a2 2 0 0 0 1.8-2.8L14 8V3" /><path d="M7.5 14h9" /></>,
  check: <><path d="M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18z" /><path d="M8.5 12l2.5 2.5 4.5-5" /></>,
};
// 제품 상세 피처 목록(아이콘 + 제목 + 한 줄 설명).
//   [2026-09-11 사용자] 아이콘 크게·눈에 띄게(evs.com/applications 철학) + 도메인 색: 안전=청량한 blue(cyan)/환경=green.
//   tone으로 색 분기(.tSafety/.tEnv). 크기·색은 Products.module.scss.
function Features({ features, tone }: { features: ProductFeature[]; tone: 'safety' | 'environment' }) {
  return (
    <ul className={s.featList}>
      {features.map((f, i) => (
        <li key={i} className={`${s.feat} ${tone === 'safety' ? s.tSafety : s.tEnv}`}>
          <span className={s.featIco} aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              {DETAIL_ICON[f.icon] || DETAIL_ICON.check}
            </svg>
          </span>
          <span className={s.featTx}><strong>{f.title}</strong><em>{f.desc}</em></span>
        </li>
      ))}
    </ul>
  );
}

// [2026-09-11 사용자] 각 제품 공통 하드웨어 스펙표(게이트웨이처럼). 상단 라인=도메인 색. specs 있는 제품만.
function SpecMini({ specs, tone }: { specs: { k: string; v: string }[]; tone: 'safety' | 'environment' }) {
  return (
    <table className={`${s.miniSpec} ${tone === 'safety' ? s.tSafety : s.tEnv}`}>
      <tbody>
        {specs.map((sp, i) => (<tr key={i}><th scope="row">{sp.k}</th><td>{sp.v}</td></tr>))}
      </tbody>
    </table>
  );
}

// 제품소개 (/products) — 목업 viaportal.evs.com 다크 디자인 실측 적용(2026-08-02).
//   구조: 히어로 → 인트로 → IoT Gateway(#gateway) → IoT 계측기기(#instruments, 세로 교차 섹션).
//   콘텐츠·이미지 = evstech /products 실측. 제품 사진은 원본 보존(contain, 무왜곡).
//   여백/리듬은 기존 리엑트(회사소개·사업소개)의 넉넉한 철학에 맞춤(사용자 2026-08-02).

// 개별 리빌 블록 — 각 블록이 스스로 관찰(.anim-scroll.is-inview)해 확실히 노출.
//   ★긴 섹션 하나에 리빌을 몰아두면 하위 블록(사양표 등)이 opacity:0 로 남던 문제 방지(2026-08-02).
const Reveal = ({ id, className = '', children }: { id?: string; className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div id={id} className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};

const Products = observer(() => {
  const { productPage } = useContent();
  const { hero, intro, gateway, instruments } = productPage;
  const { hash } = useLocation();
  const heroBgRef = useHeroParallax<HTMLDivElement>();     // 스크롤 패럴랙스(히어로 이미지 유지)

  // ── 통합 카탈로그 상태(2026-09-08) ──────────────────────────────────────────
  //  필터=제품 유형(전체/게이트웨이/계측기/안전단말기, 헤더 서브메뉴 ?type=…와 1:1).
  //  검색=이름·설명·사양·유형 텍스트 대상(향후 제품 증가 대비, 현재 7종이라 당장은 여유). [[evs-page-ux-standards]]
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');            // 서브메뉴 진입 시 초기 필터
  const [filter, setFilter] = useState<ProductType | 'all'>('all');
  const [query, setQuery] = useState('');
  // URL ?type= → 필터 초기화(문자열 값 의존: params 객체는 매 렌더 새 identity라 값으로 비교).
  useEffect(() => {
    const valid = PRODUCT_FILTERS.some((f) => f.key === typeParam);
    setFilter(valid ? (typeParam as ProductType) : 'all');
  }, [typeParam]);

  const catalog = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS_CATALOG.filter((p) => {
      if (filter !== 'all' && p.type !== filter) return false;
      if (!q) return true;
      const hay = [p.name, p.desc, p.typeLabel, ...p.specs].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [filter, query]);

  const navigate = useNavigate();
  // [2026-10-08 사용자] 기획서 v7 2-1: /products = '허브'(검색·비교·상담) → 카드 클릭 시 제품 '독립 상세페이지'로 이동.
  //   과거: 같은 페이지 앵커(#gateway 등)로 스크롤하던 방식 → 상세가 별도 URL로 승격되며 라우팅으로 변경.
  //   가스검침(t2) 등 상세 미생성 '예정' 제품은 이동 안 함. 그 외 미매핑은 도메인 페이지로 폴백.
  const onCard = (p: (typeof PRODUCTS_CATALOG)[number]) => {
    const route = PRODUCT_DETAIL_ROUTE[p.id];
    if (route) { navigate(route); return; }
    if (p.soon) return;
    navigate(p.domain === 'safety' ? '/safety' : '/environment');
  };
  // [2026-09-09 사용자] 안전 단말기 필터에서 '안전 페이지 상세'로 SPA 이동(뒤로가기 스크롤 복원 유지).
  const go = (to: string) => (e: React.MouseEvent) => { e.preventDefault(); navigate(to); };

  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const id = hash.replace('#', '');
    let cancelled = false;
    // [2026-09-09 사용자] 홈 계측기 카드 클릭 → ?type=X#제품id 로 '해당 제품 상세 블록'(#p-i1~p-i4)에 앵커.
    //   깊은 위치의 상세 블록은 초기 lenis 앵커가 로더·정착 전 타이밍에 발화돼 못 미치는 경우가 있었음(예: 905px에 멈춤).
    //   → 감시형 재발화: 늦은 시점(2.2·3.4s)까지 포함해, 목표가 상단(헤더 아래) 근처에 아직 없으면만 재스크롤(도달 시 스킵→흔들림 없음).
    // 목표: 요소를 고정 헤더(149px) 아래 정위치로. 대상에 scroll-margin-top(150~160)을 줬으므로
    //   scrollIntoView가 그 여백을 존중해 헤더 아래로 정렬한다(브라우저 네이티브 프래그먼트 스크롤과도 일치).
    //   깊은 상세 블록은 로더·정착 타이밍에 초기 스크롤이 못 미칠 수 있어, 정위치(top 120~200) 아닐 때만 감시 재발화.
    const fire = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      if (top >= 120 && top <= 200) return; // 이미 헤더 아래 정위치 → 스킵(흔들림 없음)
      // [2026-09-10] ★Lenis가 native scrollIntoView를 되돌려 못 미치던 문제(예: 418px 멈춤) 수정 →
      //   Lenis 직접 스크롤(offset -150=헤더 아래, immediate=애니메이션 충돌 없이 즉시 정위치). 없으면 native 폴백.
      const lenis = getLenis();
      if (lenis) lenis.scrollTo(el, { offset: -150, immediate: true });
      else el.scrollIntoView({ block: 'start', behavior: 'auto' });
    };
    // 늦은 이미지 로드로 대상이 아래로 밀리는 경우까지 보정(정위치면 skip이라 흔들림 없음).
    const timers = [80, 500, 1100, 2000, 3200, 4400, 6000, 8000].map((d) => window.setTimeout(fire, d));
    // ★[2026-09-10] 사용자가 스크롤(휠·터치·키)하면 자동 보정 즉시 중단 → 스크롤 후 다시 위로 튀는 문제 해결.
    const stop = () => { cancelled = true; timers.forEach(clearTimeout); };
    window.addEventListener('wheel', stop, { passive: true, capture: true, once: true });
    window.addEventListener('touchstart', stop, { passive: true, capture: true, once: true });
    window.addEventListener('keydown', stop, { capture: true, once: true });
    return () => {
      stop();
      window.removeEventListener('wheel', stop, true);
      window.removeEventListener('touchstart', stop, true);
      window.removeEventListener('keydown', stop, true);
    };
  }, [hash]);

  return (
    <main className={s.page}>
      <PageLoader />
      {/* 히어로 — 풀높이 배경이미지 + 중앙 가는 제목(자체 애니메이션) */}
      <header className={s.hero}>
        <div ref={heroBgRef} className={s.heroBg} style={{ backgroundImage: `url(${hero.image})` }} />
        <div className={s.heroOverlay} />
        <HeroCrumbs items={['HOME', '제품', '전체 제품']} />
        <div className={s.heroInner}>
          <p className={s.heroEyebrow}>{hero.slogan}</p>
          <h1 className={s.heroTitle}>{hero.sloganKo}</h1>
          <p className={s.heroSub}>{hero.sub}</p>
        </div>
      </header>

      {/* ── 통합 카탈로그(2026-09-08 사용자, koreadigital·autonics 구조 참고[[evs-ref-koreadigital-structure]]) ──
          '제품으로 찾기' 핵심 UX: 검색 + 유형 필터 탭 + 사양 중심 카드 그리드. 도메인은 옅은 색 구분자(dot)로만.
          카드 클릭 → 같은 페이지 상세 앵커로 스크롤(있을 때). 디자인=evs.com/as 계열(메인과 통일). */}
      <section id="catalog" className={`${s.section} ${s.dark2}`}>
        <div className={s.inner}>
          <Reveal className={s.catHead}>
            <p className={s.eyebrow}>PRODUCTS</p>
            <h2 className={s.catTitle}>제품으로 찾기</h2>
            <p className={s.catLead}>유형으로 좁히거나 검색으로 바로 찾으세요. 각 카드는 핵심 사양 중심으로 정리했습니다.</p>
          </Reveal>

          {/* 검색 + 필터 탭 — 검색은 향후 제품 증가 대비 상시 노출 */}
          <div className={s.catControls}>
            <div className={s.catSearch}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" />
              </svg>
              <input
                type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="제품명 · 사양 검색 (예: WBGT, 차압, LTE)" aria-label="제품 검색"
              />
            </div>
            <div className={s.catFilters} role="tablist" aria-label="제품 유형 필터">
              {PRODUCT_FILTERS.map((f) => (
                <button
                  key={f.key} role="tab" aria-selected={filter === f.key}
                  className={`${s.catTab} ${filter === f.key ? s.on : ''}`}
                  onClick={() => setFilter(f.key)}
                >{f.label}</button>
              ))}
            </div>
          </div>

          {/* 결과 그리드 */}
          {catalog.length === 0 ? (
            <p className={s.catEmpty}>‘{query}’에 해당하는 제품이 없습니다. 다른 검색어나 ‘전체’를 눌러보세요.</p>
          ) : (
            <ul className={s.catGrid}>
              {catalog.map((p) => (
                <li key={p.id} id={p.id} className={`${s.catCard} ${p.domain === 'safety' ? s.dSafety : s.dEnv} ${p.soon ? s.soon : ''}`}>
                  <button className={s.catCardBtn} onClick={() => onCard(p)}>
                    <span className={s.catThumb}>
                      {p.image
                        ? <img src={p.image} alt={p.name} loading="lazy" />
                        : <span className={s.catThumbSoon}>준비중</span>}
                    </span>
                    <span className={s.catBody}>
                      <span className={s.catTags}>
                        {/* 도메인 구분자 — dot 하나(안전=시안/환경=그린) + 유형 라벨. 색은 구분용, 장식 아님. */}
                        <span className={s.catDomain}><i className={s.catDot} />{p.domain === 'safety' ? '안전' : '환경'}</span>
                        <span className={s.catType}>{p.typeLabel}</span>
                        {p.soon && <span className={s.catSoon}>출시 예정</span>}
                      </span>
                      <span className={s.catName}>{p.name}</span>
                      <span className={s.catDesc}>{p.desc}</span>
                      <span className={s.catSpecs}>
                        {p.specs.map((sp, i) => <span key={i} className={s.catSpec}>{sp}</span>)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ── [2026-09-09 사용자] 상세 섹션은 필터 연동 — 필터에 맞는 상세만 노출(전체=다). ── */}
      {/* ── [2026-10-08 사용자] /products = 허브(기획서 v7 2-1): 제품 상세는 독립 페이지로 이관.
          과거 이 페이지의 인라인 상세(안전단말기·게이트웨이·계측기 블록)는 제거하고, 여기선 '비교 안내'·'공통 상담'만 둔다.
          (상세 진실원천 = /products/evs-g200 · /products/instruments · /products/heat-index 단일화, 중복 방지) ── */}
      {/* ④ 비교 안내 — 단품구매·견적형·현장실사형 구분 */}
      <section className={`${s.section} ${s.buyBand}`}>
        <div className={s.inner}>
          <Reveal className={s.instHead}>
            <p className={s.eyebrow}>HOW TO BUY</p>
            <h2 className={s.instTitle}>구매 방식 안내</h2>
            <p className={s.instBody}>제품 특성에 따라 구매 방식이 다릅니다. 현장 환경에 맞는 방식을 선택하세요.</p>
          </Reveal>
          {/* [2026-10-23 사용자] 라이트 웨이브 카드(Claude Design 스펙): 스타일·색상은 목표 그대로(레드/블루/퍼플),
              폰트는 사이트(Pretendard) 상속. 머리(밝은+알약+번호) → 웨이브 곡선 → 컬러 몸통(흰 글씨). */}
          <div className={s.waveGrid}>
            {[
              { label: '단품 구매', n: '01', body: '계측기 등 단품을 바로 구매합니다. 모델·수량·연동 환경을 알려주시면 견적을 안내합니다.',
                vars: { ['--body' as string]: 'linear-gradient(170deg,#C2185B 0%,#E5394B 100%)', ['--pill' as string]: 'linear-gradient(90deg,#E5394B,#F06A7C)' } },
              { label: '견적형', n: '02', body: '게이트웨이+계측기 구성처럼 수량·구성이 필요한 제품은 견적으로 안내합니다.',
                vars: { ['--body' as string]: 'linear-gradient(170deg,#0B8FB8 0%,#1F4FB8 100%)', ['--pill' as string]: 'linear-gradient(90deg,#1F4FB8,#1A8FD6)' } },
              { label: '현장실사형', n: '03', body: '설치·연동까지 필요한 경우 현장실사 후 최적 구성과 견적을 제안합니다.',
                vars: { ['--body' as string]: 'linear-gradient(170deg,#B5179E 0%,#5A23C8 100%)', ['--pill' as string]: 'linear-gradient(90deg,#6A2BD9,#C7259E)' } },
            ].map((c) => (
              <div key={c.n} className={s.waveCard} style={c.vars as React.CSSProperties}>
                <div className={s.waveHead}>
                  <span className={s.wavePill}>{c.label}</span>
                  <p className={s.waveNum}>{c.n}</p>
                </div>
                <svg viewBox="0 0 300 36" preserveAspectRatio="none" className={s.waveSvg} aria-hidden="true"><path d="M0 0H300V10C220 40 80 40 0 10Z" fill="#F1F2F5" /></svg>
                <div className={s.waveBody}><p>{c.body}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ⑤ 공통 상담 — 어떤 제품이 필요한지 모르는 고객용(기획서 2-1) · [2026-10-23] 결론 CTA(시안 악센트 탭 통일) */}
      <section className={`${s.section} ${s.finalCta}`}>
        <div className={s.inner}>
          <Reveal className={s.hubConsult}>
            <h2 className={s.catTitle}>어떤 제품이 필요한지 모르시겠나요?</h2>
            <p className={s.catLead}>현장 환경(시설·측정 대상·통신)을 알려주시면 제품 구성을 제안해 드립니다.</p>
            <div className={s.hubCta}>
              <Button variant="pill" onClick={() => navigate('/support/consult')}>실시간 상담</Button>
              <a className={s.hubGhost} onClick={() => navigate('/support/inquiry')}>견적 문의</a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
});

export default Products;
